import { Injectable, OnDestroy } from '@angular/core';
import { AngularFireAuth } from '@newgenus/fire-compat/auth';
import type firebase from 'firebase/compat/app';
import { BehaviorSubject, Subject, filter, firstValueFrom, takeUntil } from 'rxjs';
import { environment } from '../../environments/environment';
import { IssuesFirebase } from './central-firebase';

export type CentralSessionState = 'idle' | 'connecting' | 'ready' | 'error';

/** The central project's idea of who is signed in — `uid` is `centralUid` from the exchange
 *  (a sha256 of the email), NOT the environment uid. Every person-stamped field in the tracker
 *  (createdBy, reporter.uid, comment author.uid, …) must use THIS uid. */
export interface CentralUser {
    uid: string;
    email: string;
    name: string;
    env: string;
    admin: boolean;
}

interface ExchangeTokenResult {
    customToken: string;
    centralUid: string;
    email: string;
    displayName: string;
    env: string;
    admin: boolean;
}

interface ExchangeTokenErrorBody {
    error?: { status?: string; message?: string };
}

/**
 * Swaps the environment's ID token for a central custom token (OE-188 PRD) and keeps the
 * NAMED "issues" Firebase app signed in to the central project. Re-exchanges silently when the
 * env user changes and when the central session expires (its own onIdTokenChanged going null
 * while an env user is still signed in); signs the named app out when the env user signs out.
 * Never throws into a template — a failed exchange lands in `state$`/`errorMessage$` so the
 * tracker shell can show a "Can't reach the issue store" state with Retry instead of a blank
 * page. `ready()` is what every IssuesService write awaits before touching Firestore/Storage.
 */
@Injectable({ providedIn: 'root' })
export class CentralSessionService implements OnDestroy {
    private stateSubject = new BehaviorSubject<CentralSessionState>('idle');
    public readonly state$ = this.stateSubject.asObservable();

    private centralUserSubject = new BehaviorSubject<CentralUser | null>(null);
    public readonly centralUser$ = this.centralUserSubject.asObservable();

    private errorMessageSubject = new BehaviorSubject<string | null>(null);
    public readonly errorMessage$ = this.errorMessageSubject.asObservable();

    private destroy$ = new Subject<void>();
    private envUser: firebase.User | null = null;
    private exchanging = false;
    private centralAuthUnsubscribe: (() => void) | null = null;

    constructor(
        private afAuth: AngularFireAuth,
        private issuesFirebase: IssuesFirebase
    ) {
        this.afAuth.authState.pipe(takeUntil(this.destroy$)).subscribe((user) => this.onEnvUserChanged(user));

        // The central token can expire independently of the env session — watch the NAMED
        // app's own auth and re-exchange while an env user is still signed in.
        this.centralAuthUnsubscribe = this.issuesFirebase.auth.onIdTokenChanged((centralFirebaseUser) => {
            if (!centralFirebaseUser && this.envUser && this.stateSubject.value === 'ready') {
                this.exchange(this.envUser);
            }
        });
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.centralAuthUnsubscribe?.();
    }

    /** Resolves once the central session is signed in — await this before every IssuesService write. */
    public ready(): Promise<CentralUser> {
        return firstValueFrom(this.centralUserSubject.pipe(filter((u): u is CentralUser => u != null)));
    }

    public retry(): void {
        if (this.envUser) this.exchange(this.envUser);
    }

    private onEnvUserChanged(user: firebase.User | null): void {
        if (!user) {
            this.envUser = null;
            this.centralUserSubject.next(null);
            this.errorMessageSubject.next(null);
            this.stateSubject.next('idle');
            this.issuesFirebase.auth.signOut().catch(() => {
                // Already signed out / nothing to do.
            });
            return;
        }
        if (this.envUser && user.uid === this.envUser.uid && this.stateSubject.value === 'ready') {
            this.envUser = user;
            return;
        }
        this.envUser = user;
        this.exchange(user);
    }

    private async exchange(user: firebase.User): Promise<void> {
        if (this.exchanging) return;
        this.exchanging = true;
        this.stateSubject.next('connecting');
        this.errorMessageSubject.next(null);
        try {
            const idToken = await user.getIdToken();
            const response = await fetch(environment.issuesFirebase.exchangeUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ data: { idToken } }),
            });
            const body = (await response.json()) as { result?: ExchangeTokenResult } & ExchangeTokenErrorBody;
            if (!response.ok || body?.error) {
                throw new Error(body?.error?.message || `exchangeToken failed with status ${response.status}`);
            }
            const result = body.result as ExchangeTokenResult;
            await this.issuesFirebase.auth.signInWithCustomToken(result.customToken);
            this.centralUserSubject.next({
                uid: result.centralUid,
                email: result.email,
                name: result.displayName,
                env: result.env,
                admin: result.admin,
            });
            this.stateSubject.next('ready');
        } catch (ex: unknown) {
            const message = ex instanceof Error ? ex.message : "Can't reach the issue store";
            console.error('CentralSessionService: token exchange failed', ex);
            this.centralUserSubject.next(null);
            this.errorMessageSubject.next(message);
            this.stateSubject.next('error');
        } finally {
            this.exchanging = false;
        }
    }
}
